<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { NDrawer, NDrawerContent } from 'naive-ui'
import { storeToRefs } from 'pinia'
import { useThreadsStore } from '../stores/threads'
import { useWikiPagesStore } from '../stores/wiki_pages'
import { useIsMobile } from '../composables/useIsMobile'
import { useRestoreFocusOnClose } from '../composables/useRestoreFocusOnClose'
import ThreadBubbleList from './ThreadBubbleList.vue'
import ThreadComposer from './ThreadComposer.vue'
import ThreadRecentList from './ThreadRecentList.vue'

const show = defineModel<boolean>('show', { required: true })

// Elapsed-counter tick interval for the "Thinking… Ns" indicator in ThreadBubbleList.
const ELAPSED_TICK_MS = 1000
const DESKTOP_DRAWER_WIDTH = 560

// Weak-citation nudge: the parent (RouteShell) owns the drawers,
// so the "add a source" button bubbles up rather than reaching across to
// open Add-to-wiki itself.
const emit = defineEmits<{ (e: 'add-source'): void }>()

useRestoreFocusOnClose(show)

const threads = useThreadsStore()
const wikiPages = useWikiPagesStore()
const { current, recent, submitting, submitError } = storeToRefs(threads)
const { pages } = storeToRefs(wikiPages)
const router = useRouter()
const route = useRoute()
const isMobile = useIsMobile()
// Full-screen drawer on mobile so a 390px viewport doesn't side-scroll
// behind a 560px-wide panel — same fix as AddToWikiDrawer.
const drawerWidth = computed<number | string>(() => (isMobile.value ? '100%' : DESKTOP_DRAWER_WIDTH))

const content = ref('')
const canSubmit = computed(() => content.value.trim().length > 0 && !submitting.value)
const composer = ref<InstanceType<typeof ThreadComposer> | null>(null)

// Sonnet calls can run 5–20s while the tool loop walks the wiki; an
// elapsed counter signals the app is alive and gives the user a rough
// "is this stuck?" yardstick without a real progress bar.
const elapsedSeconds = ref(0)
let elapsedTimer: number | undefined

watch(submitting, (running) => {
  window.clearInterval(elapsedTimer)
  if (running) {
    elapsedSeconds.value = 0
    const startedAt = Date.now()
    elapsedTimer = window.setInterval(() => {
      elapsedSeconds.value = Math.floor((Date.now() - startedAt) / ELAPSED_TICK_MS)
    }, ELAPSED_TICK_MS)
  }
})

onBeforeUnmount(() => window.clearInterval(elapsedTimer))

onMounted(() => {
  void threads.loadRecent()
  // Cold-open the drawer (e.g. from a `?thread=` bookmark) before the
  // catalog has loaded → citation chips would render raw paths. Prime
  // it so `citationLabel` resolves titles.
  if (pages.value.length === 0) void wikiPages.fetchAll().catch(() => {})
  const threadParam = route.query.thread
  if (typeof threadParam === 'string' && threadParam.length > 0) {
    const id = Number(threadParam)
    if (Number.isFinite(id) && id > 0) {
      void threads.open(id)
      show.value = true
    }
  }
})

// URL <-> thread sync. When the current thread id changes (new submit or
// explicit open), mirror it to `?thread=ID`. When the URL param changes
// externally (back-button, bookmark), load the corresponding thread.
watch(
  () => current.value?.id ?? null,
  (id) => {
    const currentParam = route.query.thread
    const next = id != null && id > 0 ? String(id) : undefined
    if (currentParam === next) return
    router.replace({ query: { ...route.query, thread: next } })
  },
)

watch(
  () => route.query.thread,
  async (value) => {
    if (typeof value !== 'string' || value.length === 0) return
    const id = Number(value)
    if (!Number.isFinite(id) || id <= 0) return
    if (current.value?.id === id) return
    await threads.open(id)
  },
)

async function submit() {
  if (!canSubmit.value) return
  const text = content.value.trim()
  content.value = ''
  try {
    await threads.submit(text)
    await threads.loadRecent()
    // Keep focus in the composer so a follow-up doesn't need a re-click.
    await nextTick()
    composer.value?.focus()
  } catch {
    // submitError is populated; restore the composer so the user can edit + retry.
    content.value = text
  }
}

function startNewThread() {
  threads.reset()
}

function openCitation(path: string) {
  router.push(`/p/${path}`)
  show.value = false
}
</script>

<template>
  <NDrawer v-model:show="show" :width="drawerWidth" placement="right">
    <NDrawerContent :native-scrollbar="false" closable :footer-style="{ display: 'block', borderTop: 'none' }">
      <template #header>
        <div v-if="current" class="flex min-w-0 items-center gap-3">
          <h2 class="m-0 min-w-0 flex-1 truncate text-base font-semibold text-ink">{{ current.title }}</h2>
          <span class="shrink-0 text-xs text-ink-faint">
            {{ current.messages.length }} {{ current.messages.length === 1 ? 'turn' : 'turns' }}
          </span>
          <button
            type="button"
            class="inline-flex min-h-10 shrink-0 cursor-pointer items-center gap-1.5 rounded-[10px] border border-border-control bg-transparent px-3 text-sm font-medium text-ink hover:bg-surface-hover"
            @click="startNewThread"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true" fill="none" stroke="currentColor">
              <path d="M7 2v10M2 7h10" stroke-width="1.6" stroke-linecap="round" />
            </svg>
            New thread
          </button>
        </div>
        <h2 v-else class="m-0 font-serif text-[28px] font-medium leading-tight text-ink">Ask</h2>
      </template>

      <p v-if="!current" class="m-0 mb-5 leading-normal text-ink-muted">
        Answers are synthesised from your wiki pages, with numbered citations you can open.
      </p>

      <!-- Message bubbles: extracted to ThreadBubbleList (#1102) so
           this view stays under the 300-line hard limit. -->
      <ThreadBubbleList
        v-if="current && current.messages.length"
        :messages="current.messages"
        :submitting="submitting"
        :elapsed-seconds="elapsedSeconds"
        @open-citation="openCitation"
        @close="show = false"
        @add-source="emit('add-source')"
      />

      <ThreadRecentList v-if="recent.length && !current" :threads="recent" @open="threads.open($event)" />

      <template #footer>
        <ThreadComposer
          ref="composer"
          v-model="content"
          :follow-up="!!current"
          :submitting="submitting"
          :can-submit="canSubmit"
          :submit-error="submitError"
          @submit="submit"
          @stop="threads.cancel()"
        />
      </template>
    </NDrawerContent>
  </NDrawer>
</template>
